import type { ClinicalEpisode, ClinicalEvent } from './types';
import type { OfflineIdentity, OfflineVault } from './offline';
import { mergeEventPage, projectEpisodeDating, refreshPreparedResources } from './offlineSync';
import { checkOfflineProvisioning, type EpisodeSnapshot, type OfflineEpisodeContext } from './offlineContext';
export async function prepareEpisodes(
  vault: OfflineVault,
  identity: OfflineIdentity,
  ids: string[],
  token: string | null,
  guard: () => boolean,
  request: typeof fetch = fetch,
) {
  if (ids.length > 200) throw new Error('Prepare at most 200 episodes at a time.');
  const authorization = await checkOfflineProvisioning(identity.organizationId, identity.userId, token, request);
  const prefix = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
  const base = `${prefix}/api/v1/organizations/${encodeURIComponent(identity.organizationId)}/flowsheets`;
  const get = async (path: string) => {
    if (!guard()) throw new Error('Offline workspace changed.');
    const response = await request(base + path, {
      cache: 'no-store',
      signal: AbortSignal.timeout(20000),
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error(`Preparation request failed (${response.status}).`);
    return response.json();
  };
  if (!guard()) throw new Error('Offline workspace changed.');
  await refreshPreparedResources(vault, identity, token, request);
  const prepared: string[] = [];
  const failed: { id: string; reason: string }[] = [];
  for (const id of [...new Set(ids)]) {
    try {
      const metadata = (await get(`/episodes/${encodeURIComponent(id)}/preparation`)) as {
        episode: ClinicalEpisode;
        context: OfflineEpisodeContext;
      };
      if (metadata.episode.id !== id || metadata.context.pregnancy.id !== metadata.episode.pregnancy_id)
        throw new Error('Episode identity mismatch.');
      let events: ClinicalEvent[] = [];
      let after = 0;
      for (;;) {
        const page = (await get(`/episodes/${encodeURIComponent(id)}/events?after=${after}&limit=500`)) as {
          events: ClinicalEvent[];
          next_cursor: number;
          has_more: boolean;
        };
        if (
          !Number.isSafeInteger(page.next_cursor) ||
          page.next_cursor < after ||
          (page.has_more && page.next_cursor <= after)
        )
          throw new Error('Invalid preparation feed cursor.');
        events = mergeEventPage(events, page.events, id);
        after = page.next_cursor;
        if (!page.has_more) break;
      }
      if (!guard()) throw new Error('Offline workspace changed.');
      await vault.updateSnapshot<EpisodeSnapshot>(`episode:${id}`, (previous) =>
        projectEpisodeDating({
          ...previous,
          authorization,
          episode: metadata.episode,
          context: metadata.context,
          cachedAt: new Date().toISOString(),
          feedCursor: Math.max(previous?.feedCursor ?? 0, after),
          events: mergeEventPage(previous?.events ?? [], events, id),
        }),
      );
      prepared.push(id);
    } catch (error) {
      failed.push({ id, reason: error instanceof Error ? error.message : 'Preparation failed.' });
      if (!guard()) break;
    }
  }
  return { failed, prepared };
}
